import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "../AppDataTable";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpFromBracket, faLocation, faPlusCircle, faRightFromBracket, faSearch, faShareFromSquare, faSyringe, faTable } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// Load Sarabun font for PDF
const loadSarabunFont = async () => {
  try {
    //const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(`Failed to fetch Sarabun font: ${fontResponse.statusText}`);
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};


const VaccineRequisitionFundingSummary = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    WarehouseID: "",
    MonthYear: "", // Store as YYYY-MM
  });
  const [warehouses, setWarehouses] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [committeeData, setCommitteeData] = useState(null);
  const [provinceMapping, setProvinceMapping] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [vaccineDetailsMap, setVaccineDetailsMap] = useState(new Map());
  const generateMonthYearOptions = () => {
  const thaiMonths = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน",
    "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม",
    "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
  ];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 0-based → +1

  const options = [];
  for (let y = currentYear - 5; y <= currentYear + 1; y++) {
    for (let m = 1; m <= 12; m++) {
      if (y > currentYear || (y === currentYear && m > currentMonth)) {
        continue; // ข้ามเดือนในอนาคต
      }
      const value = `${y}-${String(m).padStart(2, "0")}`; // ใช้ส่ง API
      const label = `${thaiMonths[m - 1]} ${y + 543}`; // แสดงผล
      options.push({ value, label });
    }
  }
  return options.reverse(); // ให้เดือนล่าสุดอยู่ด้านบน
};

  // Fetch warehouse options
  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Warehouses Response:", response);
      if (response.result === "success") {
        const warehouseList = response.VaccineWarehousesList || [];
        setWarehouses(warehouseList);
        // Set default WarehouseID to the first warehouse, if available
        if (warehouseList.length > 0) {
          setFilterData((prev) => ({
            ...prev,
            WarehouseID: warehouseList[0].WarehouseID,
          }));
          console.log("Default WarehouseID set to:", warehouseList[0].WarehouseID);
        }
      } else {
        console.error("Failed to fetch warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  // Fetch committee data
  const fetchCommitteeData = async (warehouseID) => {
    if (!warehouseID) return;
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeCommitteeByWarehouseID",
        data: { WarehouseID: warehouseID },
        sessionId: sessionId || "",
      });
      console.log("Committee Response:", response);
      if (response.result === "success") {
        setCommitteeData(response.Committee || null);
      } else {
        console.error("Failed to fetch committee data:", response.error);
        setCommitteeData(null);
      }
    } catch (error) {
      console.error("Error fetching committee data:", error);
      setCommitteeData(null);
    }
  };

  // Fetch vaccine details for PackageSize and VaccineNameTH
  const fetchVaccineDetails = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      console.log("Vaccines List Response:", response);
      if (response.result === "success" && response.VaccinesList) {
        const detailsMap = new Map();
        response.VaccinesList.forEach((vaccine) => {
          detailsMap.set(vaccine.VaccineID, vaccine);
        });
        setVaccineDetailsMap(detailsMap);
        return detailsMap;
      }
      return new Map();
    } catch (error) {
      console.error("Error fetching vaccines list:", error);
      return new Map();
    }
  };

  // Fetch report data
  const fetchReportData = async () => {
    if (!filterData.WarehouseID) {
      alert("กรุณาเลือกคลังวัคซีน");
      return;
    }
    if (!filterData.MonthYear) {
      alert("กรุณาระบุเดือนและปี");
      return;
    }
    setIsLoading(true);
    try {
      // Fetch committee data
      await fetchCommitteeData(filterData.WarehouseID);

      // คำนวณ startDate และ endDate
      const [year, month] = filterData.MonthYear.split("-");
      const startDateObj = new Date(year, month - 1, 1);     // วันแรกของเดือน
      const endDateObj = new Date(year, month, 0);           // วันสุดท้ายของเดือน

      const startDate = formatDate(startDateObj);
      const endDate = formatDate(endDateObj);

      console.log("StartDate:", startDate);
      console.log("EndDate:", endDate);

      // Fetch vaccine details
      const vaccineMap = await fetchVaccineDetails();

      // Fetch carry-forward
      const carryForwardResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryCarryForward",
        data: {
          year: year,
          month: month,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("CarryForward Response:", carryForwardResponse);

      // Fetch inbound
      const inboundResponse = await apiService({
        method: "POST",
        url: "/getVaccineInventoryInbound",
        data: {
          StartDate: startDate,
          EndDate: endDate,
          WarehouseID: filterData.WarehouseID,
        },
        sessionId: sessionId || "",
      });
      console.log("Inbound Response:", inboundResponse);

      // Fetch requisition summary
      const requisitionResponse = await apiService({
        method: "POST",
        url: "/getVaccineRequisitionFundingSummary",
        data: {
          StartDate: startDate,
          EndDate: endDate,
        },
        sessionId: sessionId || "",
      });
      console.log("Requisition Summary Response:", requisitionResponse);

      if (
        carryForwardResponse.result === "success" &&
        inboundResponse.result === "success" &&
        requisitionResponse.result === "success"
      ) {
        const data = processReportData(
          carryForwardResponse.carryForwardList || [],
          inboundResponse.inboundList || [],
          requisitionResponse.summary || [],
          requisitionResponse.provinceMapping || {},
          vaccineMap
        );
        console.log("Processed Report Data:", data);
        setReportData(data);
        setProvinceMapping(requisitionResponse.provinceMapping || {});
      } else {
        setReportData([]);
        setProvinceMapping({});
        alert(
          "ไม่พบข้อมูล: " +
            (carryForwardResponse.error ||
              inboundResponse.error ||
              requisitionResponse.error ||
              "เกิดข้อผิดพลาดในการดึงข้อมูล")
        );
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  // Process report data
  const processReportData = (
    carryForward,
    inbound,
    requisitionSummary,
    provinceMapping,
    vaccineMap
  ) => {
    const vaccineReportMap = new Map();

    // Initialize vaccine map
    const addVaccine = (VaccineID) => {
      if (!vaccineReportMap.has(VaccineID)) {
        const details = vaccineMap.get(VaccineID);
        vaccineReportMap.set(VaccineID, {
          VaccineID,
          VaccineNameTH: details?.VaccineNameTH || "ไม่ทราบชื่อ",
          BeginningBalance: 0,
          ReceivedFromDLDBVB: 0,
          Total: 0,
          Disposal: 0,
          EndingBalance: 0,
          ...Object.keys(provinceMapping).reduce((acc, key) => {
            acc[key] = 0;
            return acc;
          }, {}),
        });
        console.log(`Added VaccineID: ${VaccineID}, VaccineNameTH: ${vaccineReportMap.get(VaccineID).VaccineNameTH}`);
      }
    };

    // Process carry-forward (BeginningBalance in doses)
    carryForward.forEach(({ VaccineID, CarryForwardQuantity }) => {
      addVaccine(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);
      const packageSize = details?.PackageSize || 1;
      vaccine.BeginningBalance += (CarryForwardQuantity || 0) * packageSize;
    });

    // Track vaccines that have data from requisition summary
    const vaccinesFromSummary = new Set();

    // Process requisition summary (ReceivedFromDLDBVB, province quantities in doses)
    requisitionSummary.forEach((item) => {
      const { 
        VaccineID, 
        VaccineNameTH: summaryName, 
        ReceivedFromDLDBVB: receivedFromDLDBVB,
        BeginningBalance: beginningBalance,
        Total: total,
        Disposal: disposal,
        EndingBalance: endingBalance,
        ...provinceData 
      } = item;
      addVaccine(VaccineID);
      vaccinesFromSummary.add(VaccineID);
      const vaccine = vaccineReportMap.get(VaccineID);
      const details = vaccineMap.get(VaccineID);

      // Use values directly from requisition summary (already calculated correctly in backend)
      // ReceivedFromDLDBVB is already in doses, no conversion needed
      if (receivedFromDLDBVB !== undefined && receivedFromDLDBVB !== null) {
        vaccine.ReceivedFromDLDBVB = receivedFromDLDBVB;
      }
      
      // Use BeginningBalance, Total, Disposal, EndingBalance from summary if available
      if (beginningBalance !== undefined && beginningBalance !== null) {
        vaccine.BeginningBalance = beginningBalance;
      }
      if (total !== undefined && total !== null) {
        vaccine.Total = total;
      }
      if (disposal !== undefined && disposal !== null) {
        vaccine.Disposal = disposal;
      }
      if (endingBalance !== undefined && endingBalance !== null) {
        vaccine.EndingBalance = endingBalance;
      }

      // Convert province quantities - these are already in the summary as Province1, Province2, etc.
      Object.keys(provinceMapping).forEach((key) => {
        if (provinceData[key] !== undefined && provinceData[key] !== null) {
          vaccine[key] = provinceData[key] || 0;
        }
      });

      // Ensure VaccineNameTH is from vaccineMap
      vaccine.VaccineNameTH = details?.VaccineNameTH || summaryName || "ไม่ทราบชื่อ";
      console.log(`Updated VaccineID: ${VaccineID}, VaccineNameTH: ${vaccine.VaccineNameTH}, ReceivedFromDLDBVB: ${vaccine.ReceivedFromDLDBVB}`);
    });

    // Note: Inbound data is no longer used for ReceivedFromDLDBVB
    // The value comes directly from requisitionSummary which is calculated correctly in the backend

    // Calculate Total, Disposal, and EndingBalance only for vaccines not in requisition summary
    for (const [vaccineID, vaccine] of vaccineReportMap) {
      if (!vaccinesFromSummary.has(vaccineID)) {
        // Calculate for vaccines not in requisition summary
        vaccine.Total = vaccine.BeginningBalance + vaccine.ReceivedFromDLDBVB;
        vaccine.Disposal = Object.keys(provinceMapping).reduce(
          (sum, key) => sum + (vaccine[key] || 0),
          0
        );
        vaccine.EndingBalance = vaccine.Total - vaccine.Disposal;
      }
    }

    const result = Array.from(vaccineReportMap.values()).filter(
      (vaccine) =>
        vaccine.BeginningBalance ||
        vaccine.ReceivedFromDLDBVB ||
        vaccine.Disposal ||
        Object.keys(provinceMapping).some((key) => vaccine[key])
    );
    console.log("Final Vaccine Report Map:", result);
    return result;
  };

  useEffect(() => {
    if (sessionId) {
      fetchWarehouses();
    }
  }, [sessionId]);

  useEffect(() => {
    if (filterData.WarehouseID) {
      fetchCommitteeData(filterData.WarehouseID);
    }
  }, [filterData.WarehouseID, sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const formatNumber = (value, options = {}) => {
    const num = Number(value);
    return num === 0 ? "-" : num.toLocaleString("th-TH", options);
  };

  // Table columns
  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringe} />
          <span>ชนิดวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faRightFromBracket} />
          <span>ยอดยกมา</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.BeginningBalance),
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faRightFromBracket} />
          <span>รับจากสทช.</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.ReceivedFromDLDBVB),
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faPlusCircle} />
          <span>รวม</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.Total),
      sortable: true,
      width: "120px",
    },
    ...Object.entries(provinceMapping).map(([key, name]) => ({
      name: (
        <HStack>
          <FontAwesomeIcon icon={faLocation} />
          <span>{name}</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row[key]),
      sortable: true,
      width: "150px",
    })),
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faArrowUpFromBracket} />
          <span>จำหน่ายบัญชี</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.Disposal),
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faShareFromSquare} />
          <span>ยอดยกไป</span>
        </HStack>
      ),
      selector: (row) => formatNumber(row.EndingBalance),
      sortable: true,
      width: "120px",
    },
  ];

  // Export to Excel
  const exportToExcel = () => {
    const wsData = [
      ...reportData.map((item) => {
        const row = {
          "รหัสวัคซีน": item.VaccineID,
          "ชนิดวัคซีน": item.VaccineNameTH,
          "ยอดยกมา": Number(item.BeginningBalance).toLocaleString("th-TH"),
          "รับจากสทช.": Number(item.ReceivedFromDLDBVB).toLocaleString("th-TH"),
          "รวม": Number(item.Total).toLocaleString("th-TH"),
          "จำหน่ายบัญชี": Number(item.Disposal).toLocaleString("th-TH"),
          "ยอดยกไป": Number(item.EndingBalance).toLocaleString("th-TH"),
        };
        Object.entries(provinceMapping).forEach(([key, name]) => {
          row[name] = Number(item[key]).toLocaleString("th-TH");
        });
        return row;
      }),
      {},
    ];
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RequisitionSummary");
    XLSX.writeFile(wb, "VaccineRequisitionFundingSummary.xlsx");
  };

  const drawMultilineText = (doc, lines, x, y, lineHeight = 6) => {
    lines.forEach((line, index) => {
      doc.text(line, x, y + index * lineHeight);
    });
  };
  // Export to PDF
  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      //doc.addFileToVFS("Sarabun-Regular.ttf", fontBase64);
      //doc.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("เงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย", 14, 10);
    doc.text("แบบ ผช.37/1", 270, 10);
    doc.text("รายงานการเบิกจ่ายชีวภัณฑ์", 14, 16);
    doc.text(
      `${
        warehouses.find((w) => w.WarehouseID === filterData.WarehouseID)
          ?.WarehouseName || "ไม่ระบุ"
      }`,
      80,
      16
    );
    doc.text("เพื่อจำหน่าย", 120, 16);
    doc.setFontSize(14);
    doc.text(
      `ประจำเดือน: ${new Date(filterData.MonthYear).toLocaleDateString("th-TH", {
        month: "long",
        year: "numeric",
      })}`,
      14,
      22
    );

    // Calculate totals
    const totals = reportData.reduce(
      (acc, item) => {
        acc.BeginningBalance += item.BeginningBalance || 0;
        acc.ReceivedFromDLDBVB += item.ReceivedFromDLDBVB || 0;
        acc.Total += item.Total || 0;
        acc.Disposal += item.Disposal || 0;
        acc.EndingBalance += item.EndingBalance || 0;
        Object.keys(provinceMapping).forEach((key) => {
          acc[key] = (acc[key] || 0) + (item[key] || 0);
        });
        return acc;
      },
      {
        BeginningBalance: 0,
        ReceivedFromDLDBVB: 0,
        Total: 0,
        Disposal: 0,
        EndingBalance: 0,
      }
    );

    // Prepare table body with total row
    const body = [
      ...reportData.map((item) => [
        item.VaccineNameTH,
        item.VaccineID,
        formatNumber(item.BeginningBalance),
        formatNumber(item.ReceivedFromDLDBVB),
        formatNumber(item.Total),
        ...Object.keys(provinceMapping).map((key) =>
          formatNumber(item[key])
        ),
        formatNumber(item.Disposal),
        formatNumber(item.EndingBalance),
      ]),
      [
        "ยอดรวม (โด๊ส)",
        "",
        formatNumber(totals.BeginningBalance),
        formatNumber(totals.ReceivedFromDLDBVB),
        formatNumber(totals.Total),
        ...Object.keys(provinceMapping).map((key) =>
          formatNumber(totals[key])
        ),
        formatNumber(totals.Disposal),
        formatNumber(totals.EndingBalance),
      ],
    ];

    // Prepare table headers
    const provinceCount = Object.keys(provinceMapping).length;
    const head = [
      [
          { content: "ชนิดวัคซีน", rowSpan: 2, colSpan:2 },
          { content: "ยอดยกมา", rowSpan: 2 },
          { content: "รับจากสทช.", rowSpan: 2 },
          { content: "รวม", rowSpan: 2 },
          { content: "จ่ายให้จังหวัด", colSpan: provinceCount },
          { content: "จำหน่ายบัญชี", rowSpan: 2 },
          { content: "ยอดยกไป", rowSpan: 2 },
      ],
      [
        ...Object.values(provinceMapping),
      ],
    ];

    // Define column styles with default width
    const columnStyles = {};
    for (let i = 0; i < 5 + provinceCount; i++) {
      columnStyles[i] = { cellWidth: 12,halign: "right" }; // Default width
    }
    columnStyles[0] = { cellWidth: 60 }; // VaccineNameTH
    columnStyles[1] = { cellWidth: 15 }; // VaccineID
    columnStyles[2] = { cellWidth: 17, halign: "right" }; // BeginningBalance
    columnStyles[3] = { cellWidth: 17 , halign: "right"}; // ReceivedFromDLDBVB
    columnStyles[4] = { cellWidth: 17, halign: "right" }; // Total

    autoTable(doc, {
      startY: 24,
      head,
      body,
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },      
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fontSize: 11,
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles,
      didParseCell: (data) => {
        if (data.section === "head" && data.row.index === 0) {
          if (data.column.index === 5) {
            data.cell.styles.halign = "center";
          }
        }
      },
    });

    // Add footer text with committee signatures
    const finalY = doc.lastAutoTable.finalY || 34;
    doc.setFontSize(11);
    drawMultilineText(
      doc,
        [
          "หมายเหตุ : เอกสารประกอบรายงานที่ส่งมาด้วย",
          "1. สำเนาใบเบิกวัคซีน จำนวน..............ฉบับ เล่มที่..............-..............เลขที่..............-..............",
          "2. สำเนาใบจ่ายวัคซีน จำนวน..............ฉบับ เล่มที่..............เลขที่..............",
          "3. หลักฐานการขออนุมัติจำหน่ายบัญชีวัคซีนประจำเดือนที่ส่งรายงาน (แตก, ชำรุด, ทำลาย)",
          "",
        ],
        14,
        finalY + 5,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ผู้จัดทำรายงาน.............................................`,
          `        (${committeeData?.CMName06 || ".............................."})`,
          `        (${committeeData?.CMPosition06 || ".............................."})`,
        ],
        150,
        finalY + 5,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ผู้ตรวจสอบรายงาน.............................................`,
          `        (${committeeData?.CMName07 || ".............................."})`,
          `        (${committeeData?.CMPosition07 || ".............................."})`,
        ],
        220,
        finalY + 5,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `คณะกรรมการตามคำสั่งที่ ${committeeData?.CMOrderNo || "..."} ได้ตรวจนับวัคซีนคงเหลือแล้ว ตรงกับยอดคงเหลือในรายงาน ผช.37/1`,
        ],
        60,
        finalY + 22,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ลงชื่อ.............................................ประธานกรรมการ`,
          `        (${committeeData?.CMName01 || ".............................."})`,
          `        (${committeeData?.CMPosition01 || ".............................."})`,
        ],
        14,
        finalY + 30,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ลงชื่อ.............................................กรรมการ `,
          `        (${committeeData?.CMName02 || ".............................."})`,
          `        (${committeeData?.CMPosition02 || ".............................."})`,
        ],
        80,
        finalY + 30,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ลงชื่อ.............................................กรรมการ `,
          `        (${committeeData?.CMName03 || ".............................."})`,
          `        (${committeeData?.CMPosition03 || ".............................."})`,
        ],
        150,
        finalY + 30,
        5 // line height
    );
    drawMultilineText(
      doc,
        [
          `ลงชื่อ.............................................กรรมการ `,
          `        (${committeeData?.CMName04 || ".............................."})`,
          `        (${committeeData?.CMPosition04 || ".............................."})`,
        ],
        200,
        finalY + 30,
        5 // line height
    );

    doc.save("VaccineRequisitionFundingSummary.pdf");
  };

  // Print table
  const printTable = () => {
    
    const provinceCount = Object.keys(provinceMapping).length;
    // Calculate totals
    const totals = reportData.reduce(
      (acc, item) => {
        acc.BeginningBalance += item.BeginningBalance || 0;
        acc.ReceivedFromDLDBVB += item.ReceivedFromDLDBVB || 0;
        acc.Total += item.Total || 0;
        acc.Disposal += item.Disposal || 0;
        acc.EndingBalance += item.EndingBalance || 0;
        Object.keys(provinceMapping).forEach((key) => {
          acc[key] = (acc[key] || 0) + (item[key] || 0);
        });
        return acc;
      },
      {
        BeginningBalance: 0,
        ReceivedFromDLDBVB: 0,
        Total: 0,
        Disposal: 0,
        EndingBalance: 0,
      }
    );
    
    const printContent = `
      <html>
        <head>
          <title>Vaccine Requisition Funding Summary</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .signature-section { margin-top: 20px; }
            .signature-section p { margin: 5px 0; }
          </style>
        </head>
        <body>
          <h3>เงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ผช.37/1</h3>
          <h3>รายงานงบการเบิกจ่ายชีวผลิตภัณฑ์ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          ${
            warehouses.find((w) => w.WarehouseID === filterData.WarehouseID)
              ?.WarehouseName || "ไม่ระบุ"
          }&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;เพื่อจำหน่าย</h3>
          <p>ประจำเดือนเดือน: ${new Date(filterData.MonthYear).toLocaleDateString("th-TH", {
            month: "long",
            year: "numeric",
          })}</p>
          <table>
            <thead>
                <tr>
                <th rowspan=2 colspan=2>ชนิดวัคซีน</th>
                <th rowspan=2>ยอดยกมา</th>
                <th rowspan=2>รับจากสทช.</th>
                <th rowspan=2>รวม</th>
                <th colSpan= ${provinceCount}>จ่ายให้จังหวัด</th>
                <th rowspan=2>จำหน่ายบัญชี</th>
                <th rowspan=2>ยอดยกไป</th>
                <th rowspan=2>หมายเหตุ</th>
              </tr>
              <tr>
                ${Object.values(provinceMapping)
                  .map((name) => `<th>${name}</th>`)
                  .join("")}
              </tr>
            </thead>
            <tbody>
              ${reportData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.VaccineID}</td>
                      <td>${item.VaccineNameTH}</td>
                      <td>${formatNumber(item.BeginningBalance)}</td>
                      <td>${formatNumber(item.ReceivedFromDLDBVB)}</td>
                      <td>${formatNumber(item.Total)}</td>
                      ${Object.keys(provinceMapping)
                        .map(
                          (key) =>
                            `<td>${formatNumber(item[key])}</td>`
                        )
                        .join("")}
                      <td>${formatNumber(item.Disposal)}</td>
                      <td>${formatNumber(item.EndingBalance)}</td>
                      <td></td>
                    </tr>
                  `
                )
                .join("")}
              <tr>
                <td></td>
                <td>ยอดรวม (โด๊ส)</td>
                <td>${formatNumber(totals.BeginningBalance)}</td>
                <td>${formatNumber(totals.ReceivedFromDLDBVB)}</td>
                <td>${formatNumber(totals.Total)}</td>
                ${Object.keys(provinceMapping)
                  .map(
                    (key) =>
                      `<td>${formatNumber(totals[key])}</td>`
                  )
                  .join("")}
                <td>${formatNumber(totals.Disposal)}</td>
                <td>${formatNumber(totals.EndingBalance)}</td>
                      <td></td>
              </tr>
            </tbody>
          </table>          
          <div class="signature-section">
            <p>หมายเหตุ : เอกสารประกอบงบที่ส่งมาด้วย</p>
            <p>1. สำเนาใบเบิกวัคซีน จำนวน..............ฉบับ เล่มที่..............-..............เลขที่..............-..............</p>
            <p>2. สำเนาใบจ่ายวัคซีน จำนวน..............ฉบับ เล่มที่..............เลขที่..............</p>
            <p>3. หลักฐานการขออนุมัติจำหน่ายบัญชีวัคซีนประจำเดือนที่ส่งรายงาน (แตก, ชำรุด, ทำลาย)</p>
          </div>
          <div class="signature-section">
            <p>ผู้จัดทำรายงาน............................................. &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ผู้ตรวจสอบรายงาน............................................. </p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName06 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName07 || ".............................."})  </p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;${committeeData?.CMPosition06 || ".............................."} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ${committeeData?.CMPosition07 || ".............................."} </p>
          </div>
          <div class="signature-section">
            <p>คณะกรรมการตามคำสั่งที่ ${committeeData?.CMOrderNo || "..."} ได้ตรวจนับวัคซีนคงเหลือแล้ว</p>
            <p>มียอดคงเหลือถูกต้องตรงกับยอดคงเหลือในรายงาน ผช.37</p>
            <p>&nbsp;</p>
            <p>ลงชื่อ.............................................ประธานกรรมการ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ลงชื่อ.............................................กรรมการ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ลงชื่อ.............................................กรรมการ &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ลงชื่อ.............................................กรรมการ</p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName01 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMName02 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${
              committeeData?.CMName03 || ".............................."})&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${
              committeeData?.CMName04 || ".............................."
            })</p>
            <p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMPosition01 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${committeeData?.CMPosition02 || ".............................."}) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${
              committeeData?.CMPosition03 || ".............................."})&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(${
              committeeData?.CMPosition04 || ".............................."
            })</p>
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานสรุปเงินทุนหมุนเวียนเพื่อการจำหน่ายวัคซีน (ระดับปศุสัตว์เขต)" />

      {/* Filter Form */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการเบิกจ่ายวัคซีน"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                คลังวัคซีน
              </FormLabel>
              <Select
                name="WarehouseID"
                value={filterData.WarehouseID}
                onChange={handleInputChange}
                placeholder={warehouses.length === 0 ? "ไม่มีคลังวัคซีน" : "เลือกคลังวัคซีน"}
                colorScheme="iBlue"
              >
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
<FormControl minW="200px" flex="1">
  <FormLabel fontSize="xl" color="gray">
    เดือน/ปี
  </FormLabel>
  <Select
    name="MonthYear"
    value={filterData.MonthYear}
    onChange={(e) =>
      setFilterData({ ...filterData, MonthYear: e.target.value })
    }
    placeholder="เลือกเดือน/ปี"
    colorScheme="iBlue"
  >
    {generateMonthYearOptions().map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </Select>
</FormControl>

          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    WarehouseID: warehouses.length > 0 ? warehouses[0].WarehouseID : "",
                    MonthYear: "",
                  })
                }
              />
              <Search onClick={fetchReportData} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Table */}
      {reportData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายงานสรุปเงินทุนหมุนเวียนเพื่อการจำหน่ายวัคซีน (ระดับปศุสัตว์เขต)"
            bgColor="iBlue.600"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={reportData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              noDataComponent="ไม่มีข้อมูลแสดงผล"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VaccineRequisitionFundingSummary;